import styles from "../../../../assets/scss/common/modal/loginModal.module.scss";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {isExistedUserAsync} from "../../../../store/login/loginSlice";
import {useAppDispatch, useModal, useUserAgent} from "../../../../hooks/hooks";
import {LoginStep} from "../LoginModal";
import {Timer} from "./Timer";
import {useCodeSender} from "./hooks/useCodeSender";
import {RequestCodeType} from "../../../../types/auth";
import {ModalType} from "../../../../types/common";
import {Trans, useTranslation} from "react-i18next";

interface Props {
  email: string;
  incorrectCodeCount: number;
  setIncorrectCodeCount: (_: number) => void;
  setEmail: (_: string) => void;
  changeLoginStep: (_: LoginStep) => void;
  isBackAfterSendingCode: boolean;
  setIsBackAfterSendingCode: (_: boolean) => void;
}

export function EmailStep({
  email,
  incorrectCodeCount,
  setIncorrectCodeCount,
  setEmail,
  changeLoginStep,
  isBackAfterSendingCode,
  setIsBackAfterSendingCode,
}: Props) {
  const { t } = useTranslation("signupModal");
  // const SEND_BLOCK_SEC = 2;
  const SEND_BLOCK_SEC = 30;
  const dispatch = useAppDispatch();
  const emailFormRef = useRef<HTMLFormElement>(null);
  const { openAlertModal, openModal, openConfirmModal, closeModal } = useModal();

  const [alertMessage, setAlertMessage] = useState("");
  const [canClickSendBtn, setCanClickSendBtn] = useState(true);

  const { sendCode } = useCodeSender();
  const isExistedUser = useCallback(
    async (emailAddr: string) => {
      try {
        const result = await dispatch(isExistedUserAsync(emailAddr)).unwrap();
        return result;
      } catch (error) {
        const errorObj: any = JSON.parse((error as Error).message);
        switch (errorObj?.errorCode?.httpCode) {
          case 477:
            setAlertMessage(t("warning.email.exit"));
            break;
          case 472:
            setAlertMessage(t("warning.email.blocked"));
            break;
          default:
            openAlertModal(null);
        }

        throw error;
      }
    },
    [dispatch, openAlertModal, t]
  );

  const onClickSendingLoginCodeBtn = () => {
    if (canClickSendBtn) {
      const trimmedEmail = email.trim();
      if (trimmedEmail === "") {
        setAlertMessage(t('warning.email.empty'));
        return;
      }

      const isValidEmailForm = (emailFormRef.current as HTMLFormElement).reportValidity();
      if (!isValidEmailForm) {
        setAlertMessage(t('warning.email.invalid'));
        return;
      }
      isExistedUser(trimmedEmail).then(({result}: { result: boolean }) => {
        if (result) {
          changeLoginStep(LoginStep.AUTH_WAY);
        } else {
          openConfirmModal(
              {
                title: t("modal.email.title"),
                desc: t("modal.email.signup"),
              },
              () => {
                sendCode(email, RequestCodeType.EMAIL)
                    .then()
                    .catch();
                localStorage.setItem('email', email)
                closeModal();
                openModal(ModalType.SIGN_UP)
              }
          );
        }
      });

    }
  };

  useEffect(() => {
    if (!isBackAfterSendingCode) {
      return;
    }

    setCanClickSendBtn(false);

    if (incorrectCodeCount >= 5) {
      setAlertMessage(t("warning.email.continuousWrong"));
    } else {
      setAlertMessage(t("warning.email.waiting"));
    }
  }, [isBackAfterSendingCode, incorrectCodeCount, t]);

  const doAtTheEndTimer = useCallback(() => {
    setCanClickSendBtn(true);
    setIncorrectCodeCount(0);
    setAlertMessage("");
    setIsBackAfterSendingCode(false);
  }, [setIncorrectCodeCount, setIsBackAfterSendingCode]);

  const {isDesktop, isTablet} = useUserAgent();

  const handleSetEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    const emailValue = e.target.value.replace(/\s/g, ""); // 모든 공백 제거
    setEmail(emailValue);
  };

  return (
    <div className={styles.textDesc}>
      <div className={styles.desc}>
          <Trans t={t} i18nKey={isDesktop ? "signupAndLogin.desc1" : isTablet ? "signupAndLogin.desc1Ta" : "signupAndLogin.desc1Mo"}
                 components={{accent: <span className={styles.accent} />,br : <br />}}
          />
      </div>
      <form className={styles.emailForm} onSubmit={(e) => e.preventDefault()} ref={emailFormRef}>
        <input
          type="email"
          className={styles.input}
          value={email}
          name="email"
          onInvalid={(e) => e.preventDefault()}
          onChange={handleSetEmail}
          placeholder={t('signupAndLogin.form.email.placeholder')}
        ></input>
        {canClickSendBtn ? (
          <></>
        ) : (
          <Timer
            initialMilliseconds={SEND_BLOCK_SEC * 1000}
            styleClassName={styles.timer}
            doAtTheEnd={doAtTheEndTimer}
          />
        )}
        {alertMessage ? <span className={styles.invalidEmailAddr}>{alertMessage}</span> : <></>}
        <button className={styles.button} onClick={onClickSendingLoginCodeBtn}>
          {t('btn.next')}
        </button>
      </form>
    </div>
  );
}
